package presentation.screens.decide

import analytics.Analytics
import androidx.lifecycle.SavedStateHandle
import androidx.lifecycle.viewModelScope
import androidx.navigation.toRoute
import com.kinoshazam.Navigator
import com.kinoshazam.analytics.AppEvents
import com.kinoshazam.model.Route
import com.kinoshazam.taste.TasteCatalogItem
import com.kinoshazam.taste.TasteInteractor
import com.kinoshazam.taste.TastePartyCreateRequest
import com.kinoshazam.taste.TastePartyDto
import com.kinoshazam.taste.TastePickDto
import com.kinoshazam.taste.TasteSessionRequest
import com.kinoshazam.taste.TasteSignalKind
import com.kinoshazam.taste.TasteTitleRef
import com.kinoshazam.taste.defaultPartyCapacity
import com.kinoshazam.taste.isLocked
import com.kinoshazam.taste.isPartyCompany
import crashreporting.CrashReporter
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.isActive
import kotlinx.coroutines.launch
import utils.BaseViewModel
import utils.launch

class DecideViewModel(
    private val tasteInteractor: TasteInteractor,
    private val navigator: Navigator,
    private val analytics: Analytics,
    private val crashReporter: CrashReporter,
    savedStateHandle: SavedStateHandle,
) : BaseViewModel<DecideState, DecideMVIAction>() {

    private var committedSeeds: List<TasteTitleRef> = emptyList()
    private var partyPollJob: Job? = null
    private val initialPartyCode = savedStateHandle.toRoute<Route.Dialog.Decide>()
        .partyCode
        .trim()
        .uppercase()
        .ifBlank { null }

    override fun initState(): DecideState = DecideState(
        joiningPartyCode = initialPartyCode,
    )

    init {
        if (initialPartyCode != null) {
            openInvite(initialPartyCode)
        } else {
            loadProfile()
        }
    }

    override fun onAction(action: DecideMVIAction) {
        when (action) {
            is DecideMVIAction.Back -> onBack()
            is DecideMVIAction.Next -> onNext()
            is DecideMVIAction.Reload -> retryAfterError()
            is DecideMVIAction.LoadMoreSeeds -> {
                if (!stateValue.isLoadingSeeds && !stateValue.seedsExhausted) {
                    analytics.track(AppEvents.tasteSeedsMoreLoaded(stateValue.seedPage + 1))
                }
                loadCandidates(reset = false)
            }
            is DecideMVIAction.EditSeeds -> {
                analytics.track(AppEvents.tasteSeedsEdited())
                openSeeds()
            }
            is DecideMVIAction.SelectDuration -> updateState { copy(duration = action.value) }
            is DecideMVIAction.SelectCompany -> updateState { copy(company = action.value) }
            is DecideMVIAction.SelectMood -> updateState { copy(mood = action.value) }
            is DecideMVIAction.ToggleTag -> updateState {
                copy(
                    excludeTags = if (action.tag in excludeTags) {
                        excludeTags - action.tag
                    } else {
                        excludeTags + action.tag
                    }
                )
            }
            is DecideMVIAction.ToggleNoSubtitles -> updateState { copy(noSubtitles = !noSubtitles) }
            is DecideMVIAction.ToggleExcludeWatched -> updateState { copy(excludeWatched = !excludeWatched) }
            is DecideMVIAction.ToggleHighRating -> updateState { copy(highRating = !highRating) }
            is DecideMVIAction.ToggleSeed -> toggleSeed(action.item)
            is DecideMVIAction.WatchPick -> onWatchPick()
            is DecideMVIAction.AnotherPick -> rejectPick(TasteSignalKind.SKIPPED)
            is DecideMVIAction.NotForMe -> rejectPick(TasteSignalKind.NOT_INTERESTED)
            is DecideMVIAction.AlreadyWatched -> rejectPick(TasteSignalKind.ALREADY_WATCHED)
            is DecideMVIAction.RequestExit -> updateState { copy(askExitConfirm = true) }
            is DecideMVIAction.DismissExit -> updateState { copy(askExitConfirm = false) }
            is DecideMVIAction.ConfirmExit -> {
                analytics.track(AppEvents.tasteExited(stateValue.step.name.lowercase()))
                updateState { copy(askExitConfirm = false) }
                leaveAndClose()
            }
            is DecideMVIAction.CopyInviteLink -> {
                stateValue.party?.code?.let { analytics.track(AppEvents.tastePartyInviteCopied(it)) }
                updateState { copy(linkCopied = true) }
            }
            is DecideMVIAction.HostPickNow -> hostPickNow()
            is DecideMVIAction.LockPick -> lockCurrentPick()
            is DecideMVIAction.SetPartyCapacity -> setPartyCapacity(action.capacity)
            is DecideMVIAction.UpdateJoinCode -> updateState {
                copy(
                    joinCodeDraft = normalizePartyCode(action.value),
                    joinFailed = false,
                )
            }
            is DecideMVIAction.SubmitJoinCode -> submitJoinCode()
            is DecideMVIAction.StartOwnPick -> startOwnPick()
        }
    }

    private fun retryAfterError() {
        analytics.track(AppEvents.tasteRetry(stateValue.step.name.lowercase()))
        when (stateValue.step) {
            DecideStep.PICK -> if (stateValue.party != null) {
                retryPartyPick()
            } else {
                requestPick(resetSession = true)
            }
            DecideStep.LOBBY -> if (stateValue.party?.canPick == true) {
                hostPickNow()
            } else if (stateValue.party != null || stateValue.joiningPartyCode != null) {
                refreshParty(userInitiated = true)
            } else {
                loadProfile()
            }
            DecideStep.SEEDS -> loadCandidates(reset = true)
            else -> if (stateValue.hallNotFound) {
                val code = normalizePartyCode(stateValue.joinCodeDraft)
                    .ifBlank { stateValue.joiningPartyCode.orEmpty() }
                if (code.length >= MIN_JOIN_CODE) {
                    openInvite(code)
                } else {
                    startOwnPick()
                }
            } else if (stateValue.joiningPartyCode != null) {
                openInvite(stateValue.joiningPartyCode!!)
            } else {
                loadProfile()
            }
        }
    }

    private fun retryPartyPick() {
        val party = stateValue.party
        if (party == null) {
            requestPick(resetSession = true)
            return
        }
        updateState {
            copy(
                isSaving = true,
                isLoading = true,
                isError = false,
                pickLoadFailed = false,
                step = DecideStep.PICK,
            )
        }
        launch(onError = {
            crashReporter.capture(it)
            failPickLoad()
        }) {
            val snapshot = tasteInteractor.partyPick(party.code, emptyList())
            if (snapshot == null) {
                failPickLoad()
                return@launch
            }
            applyParty(snapshot)
            if (snapshot.pick != null) {
                showPartyPick(snapshot)
            } else {
                failPickLoad()
            }
        }
    }

    private fun failPickLoad() {
        analytics.track(AppEvents.tastePickFailed(party = stateValue.party != null))
        updateState {
            copy(
                isLoading = false,
                isSaving = false,
                isError = false,
                pickLoadFailed = true,
                pick = null,
                step = DecideStep.PICK,
            )
        }
    }

    private suspend fun refreshSessionForPick(): Boolean {
        val duration = stateValue.duration ?: return false
        val company = stateValue.company ?: return false
        val mood = stateValue.mood ?: return false
        val session = tasteInteractor.saveSession(
            TasteSessionRequest(
                duration = duration,
                company = company,
                mood = mood,
                excludeTags = stateValue.excludeTags.toList(),
                noSubtitles = stateValue.noSubtitles,
                excludeWatched = stateValue.excludeWatched,
                highRating = stateValue.highRating,
            )
        )
        return session != null
    }

    private fun loadProfile() {
        updateState { copy(isLoading = true, isError = false) }
        launch(onError = {
            crashReporter.capture(it)
            trackError("profile")
            updateState { copy(isLoading = false, isError = true) }
        }) {
            val profile = tasteInteractor.profile()
            val cached = tasteInteractor.cachedSeeds()
            val seeds = profile.seeds.ifEmpty { cached }.take(DecideState.MAX_SEEDS)
            committedSeeds = seeds
            updateState {
                copy(
                    isLoading = false,
                    selectedSeeds = seeds,
                    completeness = profile.completeness,
                    duration = profile.latestSession?.duration ?: duration,
                    company = profile.latestSession?.company ?: company,
                    mood = profile.latestSession?.mood ?: mood,
                    excludeTags = profile.latestSession?.excludeTags?.toSet() ?: excludeTags,
                    noSubtitles = profile.latestSession?.noSubtitles ?: noSubtitles,
                    excludeWatched = profile.latestSession?.excludeWatched ?: excludeWatched,
                    highRating = profile.latestSession?.highRating ?: highRating,
                )
            }
        }
    }

    private fun submitJoinCode() {
        val code = normalizePartyCode(stateValue.joinCodeDraft)
        if (code.length < MIN_JOIN_CODE) {
            updateState { copy(joinFailed = true) }
            return
        }
        openInvite(code)
    }

    private fun startOwnPick() {
        analytics.track(AppEvents.tasteStartOwnPick())
        updateState {
            copy(
                hallNotFound = false,
                joinFailed = false,
                joinCodeDraft = "",
                joiningPartyCode = null,
                isError = false,
                isLoading = false,
                step = DecideStep.DURATION,
            )
        }
        loadProfile()
    }

    private fun failHallNotFound(code: String) {
        analytics.track(AppEvents.tastePartyJoinFailed(code))
        updateState {
            copy(
                isLoading = false,
                isSaving = false,
                isError = false,
                joinFailed = true,
                hallNotFound = true,
                joinCodeDraft = code,
                joiningPartyCode = null,
                party = null,
                step = DecideStep.DURATION,
            )
        }
    }

    private fun openInvite(code: String) {
        updateState {
            copy(
                isLoading = true,
                isError = false,
                joinFailed = false,
                hallNotFound = false,
                joiningPartyCode = code,
                joinCodeDraft = code,
            )
        }
        launch(onError = {
            crashReporter.capture(it)
            failHallNotFound(code)
        }) {
            val snapshot = tasteInteractor.party(code)
            if (snapshot == null) {
                failHallNotFound(code)
                return@launch
            }
            applyParty(snapshot)
            val profile = tasteInteractor.profile()
            val cached = tasteInteractor.cachedSeeds()
            val seeds = profile.seeds.ifEmpty { cached }.take(DecideState.MAX_SEEDS)
            committedSeeds = seeds
            updateState {
                copy(
                    selectedSeeds = seeds,
                    completeness = profile.completeness,
                    duration = snapshot.duration,
                    company = snapshot.company,
                    mood = snapshot.mood,
                    excludeTags = snapshot.excludeTags.toSet(),
                    noSubtitles = snapshot.noSubtitles,
                    excludeWatched = snapshot.excludeWatched,
                    highRating = snapshot.highRating,
                )
            }
            when {
                snapshot.pick != null && snapshot.youAreMember -> showPartyPick(snapshot)
                seeds.size >= DecideState.MIN_SEEDS ||
                    (profile.completeness.favoriteCount >= DecideState.MIN_SEEDS) -> {
                    joinCurrentParty()
                }
                else -> {
                    updateState { copy(isLoading = false, step = DecideStep.SEEDS) }
                    loadCandidatesIfNeeded()
                }
            }
        }
    }

    private fun joinCurrentParty() {
        val code = stateValue.party?.code ?: stateValue.joiningPartyCode ?: return
        updateState { copy(isSaving = true, isError = false) }
        launch(onError = {
            crashReporter.capture(it)
            trackError("party_join")
            updateState { copy(isSaving = false, isError = true) }
        }) {
            if (stateValue.selectedSeeds.size >= DecideState.MIN_SEEDS) {
                tasteInteractor.replaceSeeds(stateValue.selectedSeeds)
                committedSeeds = stateValue.selectedSeeds
                analytics.track(AppEvents.tasteSeedsSaved(committedSeeds.size))
            }
            val joined = tasteInteractor.joinParty(code)
            if (joined == null) {
                failHallNotFound(code)
                return@launch
            }
            analytics.track(AppEvents.tastePartyJoined(joined.code, joined.capacity))
            applyParty(joined)
            if (joined.pick != null) {
                showPartyPick(joined)
            } else {
                updateState { copy(isSaving = false, isLoading = false, step = DecideStep.LOBBY) }
                startPartyPoll(joined.code)
            }
        }
    }

    private fun loadCandidates(reset: Boolean) {
        if (stateValue.isLoadingSeeds) return
        if (!reset && stateValue.seedsExhausted) return
        val page = if (reset) 1 else stateValue.seedPage + 1
        updateState {
            copy(
                isLoadingSeeds = true,
                candidates = if (reset) emptyList() else candidates,
                seedsExhausted = if (reset) false else seedsExhausted,
            )
        }
        launch(onError = {
            crashReporter.capture(it)
            updateState { copy(isLoadingSeeds = false) }
        }) {
            val loaded = tasteInteractor.seedCandidates(page)
            val existing = if (reset) emptyList() else stateValue.candidates
            val merged = (existing + loaded).distinctBy { it.videoId to it.isTvShow }
            val noNewItems = loaded.isNotEmpty() && merged.size == existing.size
            val exhausted = page >= MAX_SEED_PAGE || noNewItems || (loaded.isEmpty() && page > 1)
            updateState {
                copy(
                    isLoadingSeeds = false,
                    candidates = merged,
                    seedPage = page,
                    seedsExhausted = exhausted,
                )
            }
            if (!exhausted && loaded.isNotEmpty() && merged.size < MIN_SEED_GRID) {
                loadCandidates(reset = false)
            }
        }
    }

    private fun loadCandidatesIfNeeded() {
        if (stateValue.candidates.isNotEmpty() || stateValue.isLoadingSeeds) return
        loadCandidates(reset = true)
    }

    private fun openSeeds() {
        updateState { copy(step = DecideStep.SEEDS, isError = false, isLoading = false) }
        loadCandidatesIfNeeded()
    }

    private fun onBack() {
        val previous = when (stateValue.step) {
            DecideStep.DURATION -> {
                navigator.back()
                return
            }
            DecideStep.COMPANY -> DecideStep.DURATION
            DecideStep.MOOD -> DecideStep.COMPANY
            DecideStep.CONSTRAINTS -> DecideStep.MOOD
            DecideStep.SEEDS -> {
                if (stateValue.joiningPartyCode != null && stateValue.party == null) {
                    updateState { copy(askExitConfirm = true) }
                    return
                }
                updateState {
                    copy(
                        step = DecideStep.CONSTRAINTS,
                        selectedSeeds = committedSeeds,
                        isError = false,
                    )
                }
                return
            }
            DecideStep.LOBBY -> {
                updateState { copy(askExitConfirm = true) }
                return
            }
            DecideStep.PICK -> if (stateValue.party != null) DecideStep.LOBBY else DecideStep.CONSTRAINTS
        }
        updateState { copy(step = previous, isError = false) }
        if (previous == DecideStep.LOBBY) {
            stateValue.party?.code?.let(::startPartyPoll)
        }
    }

    private fun onNext() {
        when (stateValue.step) {
            DecideStep.DURATION -> if (stateValue.duration != null) {
                analytics.track(
                    AppEvents.tasteStepCompleted("duration", stateValue.duration!!.name.lowercase()),
                )
                updateState { copy(step = DecideStep.COMPANY) }
            }
            DecideStep.COMPANY -> if (stateValue.company != null) {
                analytics.track(
                    AppEvents.tasteStepCompleted("company", stateValue.company!!.name.lowercase()),
                )
                updateState { copy(step = DecideStep.MOOD) }
            }
            DecideStep.MOOD -> if (stateValue.mood != null) {
                analytics.track(
                    AppEvents.tasteStepCompleted("mood", stateValue.mood!!.name.lowercase()),
                )
                updateState { copy(step = DecideStep.CONSTRAINTS) }
            }
            DecideStep.CONSTRAINTS -> {
                analytics.track(
                    AppEvents.tasteConstraintsSet(
                        excludeTags = stateValue.excludeTags.toList(),
                        noSubtitles = stateValue.noSubtitles,
                        excludeWatched = stateValue.excludeWatched,
                        highRating = stateValue.highRating,
                        hasSeeds = stateValue.canContinueSeeds,
                    )
                )
                if (stateValue.canContinueSeeds) persistAndContinue() else openSeeds()
            }
            DecideStep.SEEDS -> if (stateValue.canContinueSeeds) {
                if (stateValue.joiningPartyCode != null || stateValue.party != null) {
                    joinCurrentParty()
                } else {
                    persistAndContinue()
                }
            }
            DecideStep.LOBBY -> hostPickNow()
            DecideStep.PICK -> openPick()
        }
    }

    private fun toggleSeed(item: TasteCatalogItem) {
        val ref = TasteTitleRef(
            videoId = item.videoId,
            isTvShow = item.isTvShow,
            title = item.title,
            imageUrl = item.imageUrl,
        )
        val exists = stateValue.selectedSeeds.any { it.videoId == ref.videoId && it.isTvShow == ref.isTvShow }
        val next = if (exists) {
            stateValue.selectedSeeds.filterNot { it.videoId == ref.videoId && it.isTvShow == ref.isTvShow }
        } else if (stateValue.selectedSeeds.size >= DecideState.MAX_SEEDS) {
            stateValue.selectedSeeds
        } else {
            stateValue.selectedSeeds + ref
        }
        if (next.size == stateValue.selectedSeeds.size) return
        analytics.track(
            AppEvents.tasteSeedToggled(
                videoId = ref.videoId,
                added = !exists,
                seedCount = next.size,
            )
        )
        updateState { copy(selectedSeeds = next) }
    }

    private fun persistAndContinue() {
        val duration = stateValue.duration ?: return
        val company = stateValue.company ?: return
        val mood = stateValue.mood ?: return
        updateState { copy(isSaving = true, isError = false) }
        launch(onError = {
            crashReporter.capture(it)
            trackError("session")
            updateState { copy(isSaving = false, isLoading = false, isError = true) }
        }) {
            val session = tasteInteractor.saveSession(
                TasteSessionRequest(
                    duration = duration,
                    company = company,
                    mood = mood,
                    excludeTags = stateValue.excludeTags.toList(),
                    noSubtitles = stateValue.noSubtitles,
                    excludeWatched = stateValue.excludeWatched,
                    highRating = stateValue.highRating,
                )
            )
            if (session == null) {
                trackError("session")
                updateState { copy(isSaving = false, isLoading = false, isError = true) }
                return@launch
            }
            if (stateValue.selectedSeeds.size >= DecideState.MIN_SEEDS) {
                tasteInteractor.replaceSeeds(stateValue.selectedSeeds)
                committedSeeds = stateValue.selectedSeeds
                analytics.track(AppEvents.tasteSeedsSaved(committedSeeds.size))
            }
            analytics.track(
                AppEvents.tasteSessionSaved(
                    duration = duration.name,
                    company = company.name,
                    mood = mood.name,
                    seedCount = stateValue.selectedSeeds.size,
                    readyForPick = true,
                )
            )
            if (company.isPartyCompany()) {
                val party = tasteInteractor.createParty(
                    TastePartyCreateRequest(
                        duration = duration,
                        company = company,
                        mood = mood,
                        excludeTags = stateValue.excludeTags.toList(),
                        noSubtitles = stateValue.noSubtitles,
                        excludeWatched = stateValue.excludeWatched,
                        highRating = stateValue.highRating,
                        capacity = company.defaultPartyCapacity(),
                    )
                )
                if (party == null) {
                    trackError("party_create")
                    updateState { copy(isSaving = false, isLoading = false, isError = true) }
                    return@launch
                }
                analytics.track(AppEvents.tastePartyCreated(party.code, party.capacity))
                applyParty(party)
                updateState { copy(isSaving = false, isLoading = false, step = DecideStep.LOBBY) }
                startPartyPoll(party.code)
            } else {
                loadPickAfterSession()
            }
        }
    }

    private suspend fun loadPickAfterSession(
        exclude: List<TasteTitleRef> = stateValue.shownIds,
        resetShown: Boolean = false,
    ) {
        updateState {
            copy(
                isSaving = false,
                isLoading = true,
                isError = false,
                pickLoadFailed = false,
                step = DecideStep.PICK,
            )
        }
        val pick = tasteInteractor.pick(exclude)
        if (pick == null) {
            failPickLoad()
            return
        }
        analytics.track(
            AppEvents.tastePickShown(
                videoId = pick.videoId,
                reasonKey = pick.reasonKey,
            )
        )
        updateState {
            copy(
                isLoading = false,
                pickLoadFailed = false,
                pick = pick,
                shownIds = if (resetShown) listOf(pick.toRef()) else shownIds + pick.toRef(),
            )
        }
    }

    private fun requestPick(resetSession: Boolean = false) {
        updateState {
            copy(
                isLoading = true,
                isError = false,
                pickLoadFailed = false,
                step = DecideStep.PICK,
            )
        }
        launch(onError = {
            crashReporter.capture(it)
            failPickLoad()
        }) {
            if (resetSession) {
                if (!refreshSessionForPick()) {
                    failPickLoad()
                    return@launch
                }
                loadPickAfterSession(exclude = emptyList(), resetShown = true)
            } else {
                loadPickAfterSession()
            }
        }
    }

    private fun hostPickNow() {
        val party = stateValue.party ?: return
        if (party.pick != null) {
            showPartyPick(party)
            return
        }
        if (!party.canPick) {
            updateState { copy(isSaving = false, isLoading = false) }
            return
        }
        analytics.track(AppEvents.tastePartyHostPick(party.code))
        updateState {
            copy(
                isSaving = true,
                isError = false,
                pickLoadFailed = false,
                isLoading = true,
                step = DecideStep.PICK,
            )
        }
        launch(onError = {
            crashReporter.capture(it)
            failPickLoad()
        }) {
            val snapshot = tasteInteractor.partyPick(party.code, stateValue.shownIds)
            if (snapshot == null) {
                failPickLoad()
                return@launch
            }
            applyParty(snapshot)
            if (snapshot.pick != null) {
                showPartyPick(snapshot)
            } else {
                failPickLoad()
            }
        }
    }

    private fun setPartyCapacity(capacity: Int) {
        val party = stateValue.party ?: return
        if (!party.youAreHost) return
        analytics.track(AppEvents.tastePartyCapacitySet(party.code, capacity))
        launch(onError = { crashReporter.capture(it) }) {
            val snapshot = tasteInteractor.setPartyCapacity(party.code, capacity) ?: return@launch
            applyParty(snapshot)
        }
    }

    private fun onWatchPick() {
        val current = stateValue.pick ?: return
        val party = stateValue.party
        analytics.track(
            AppEvents.tastePickWatch(
                videoId = current.videoId,
                party = party != null,
                locked = party?.isLocked() == true,
            )
        )
        if (party != null && !party.isLocked()) {
            voteOnPick(TasteSignalKind.ACCEPTED)
            return
        }
        openPick()
    }

    private fun lockCurrentPick() {
        val party = stateValue.party ?: return
        if (!party.canLock) return
        updateState { copy(isSaving = true, isError = false) }
        launch(onError = {
            crashReporter.capture(it)
            trackError("lock")
            updateState { copy(isSaving = false, isError = true) }
        }) {
            val snapshot = tasteInteractor.lockPartyPick(party.code)
            if (snapshot == null) {
                trackError("lock")
                updateState { copy(isSaving = false, isLoading = false, isError = true) }
                return@launch
            }
            applyParty(snapshot)
            snapshot.pick?.let {
                analytics.track(AppEvents.tastePartyLocked(it.videoId, snapshot.code))
            }
            updateState {
                copy(
                    isSaving = false,
                    isLoading = false,
                    pick = snapshot.pick ?: pick,
                    party = snapshot,
                )
            }
        }
    }

    private fun rejectPick(kind: TasteSignalKind) {
        val current = stateValue.pick ?: return
        val party = stateValue.party
        analytics.track(
            AppEvents.tastePickRejected(
                videoId = current.videoId,
                kind = kind.name.lowercase(),
                party = party != null,
            )
        )
        if (party != null) {
            voteOnPick(kind)
            return
        }
        val rejectedRef = current.toRef()
        updateState { copy(shownIds = shownIds + rejectedRef) }
        updateState { copy(isLoading = true, isError = false, pickLoadFailed = false) }
        launch(onError = {
            crashReporter.capture(it)
            failPickLoad()
        }) {
            tasteInteractor.recordSignal(
                videoId = current.videoId,
                isTvShow = current.isTvShow,
                kind = kind,
                sessionId = current.sessionId,
            )
            loadPickAfterSession()
        }
    }

    private fun voteOnPick(kind: TasteSignalKind) {
        val current = stateValue.pick ?: return
        val party = stateValue.party ?: return
        if (party.isLocked() && !party.canForceNext) return
        analytics.track(
            AppEvents.tastePartyVote(
                videoId = current.videoId,
                kind = kind.name.lowercase(),
                code = party.code,
            )
        )
        updateState { copy(isSaving = true, isError = false, isLoading = true) }
        launch(onError = {
            crashReporter.capture(it)
            trackError("vote")
            updateState { copy(isSaving = false, isLoading = false, isError = true) }
        }) {
            val snapshot = tasteInteractor.partySignal(
                code = party.code,
                videoId = current.videoId,
                isTvShow = current.isTvShow,
                kind = kind,
                sessionId = current.sessionId,
            )
            if (snapshot == null) {
                updateState { copy(isSaving = false, isLoading = false) }
                return@launch
            }
            applyParty(snapshot)
            val nextPick = snapshot.pick
            if (nextPick != null && nextPick.videoId != current.videoId) {
                showPartyPick(snapshot)
            } else {
                updateState {
                    copy(
                        isSaving = false,
                        isLoading = false,
                        pick = nextPick ?: pick,
                        party = snapshot,
                    )
                }
            }
        }
    }

    private fun openPick() {
        val pick = stateValue.pick ?: return
        val id = pick.videoId.toIntOrNull() ?: return
        analytics.track(
            AppEvents.tastePickOpened(
                videoId = pick.videoId,
                isSerial = pick.isTvShow,
            )
        )
        navigator.navigateTo(Route.Dialog.Details(id = id, isSerial = pick.isTvShow))
    }

    private fun applyParty(snapshot: TastePartyDto) {
        updateState {
            copy(
                party = snapshot,
                joiningPartyCode = snapshot.code,
                duration = snapshot.duration,
                company = snapshot.company,
                mood = snapshot.mood,
                excludeTags = snapshot.excludeTags.toSet(),
                noSubtitles = snapshot.noSubtitles,
                excludeWatched = snapshot.excludeWatched,
                highRating = snapshot.highRating,
            )
        }
    }

    private fun showPartyPick(snapshot: TastePartyDto) {
        val pick = snapshot.pick ?: return
        analytics.track(
            AppEvents.tastePickShown(
                videoId = pick.videoId,
                reasonKey = pick.reasonKey,
            )
        )
        updateState {
            copy(
                isLoading = false,
                isSaving = false,
                isError = false,
                pickLoadFailed = false,
                step = DecideStep.PICK,
                pick = pick,
                party = snapshot,
            )
        }
        startPartyPoll(snapshot.code)
    }

    private fun refreshParty(userInitiated: Boolean = false) {
        val code = stateValue.party?.code ?: stateValue.joiningPartyCode
        if (code.isNullOrBlank()) {
            if (userInitiated) {
                updateState { copy(isLoading = false, isError = true) }
            }
            return
        }
        if (userInitiated) {
            updateState { copy(isLoading = true, isError = false) }
        }
        launch(onError = {
            crashReporter.capture(it)
            if (userInitiated) {
                trackError("party_refresh")
                updateState { copy(isLoading = false, isError = true) }
            }
        }) {
            val snapshot = tasteInteractor.party(code)
            if (snapshot == null) {
                if (userInitiated) {
                    updateState { copy(isLoading = false, isError = true) }
                }
                return@launch
            }
            applyParty(snapshot)
            val partyPick = snapshot.pick
            when {
                partyPick != null && stateValue.step != DecideStep.SEEDS -> {
                    if (stateValue.pick?.videoId != partyPick.videoId) {
                        showPartyPick(snapshot)
                    } else {
                        updateState {
                            copy(
                                pick = partyPick,
                                party = snapshot,
                                isLoading = false,
                                isError = false,
                                pickLoadFailed = false,
                                isSaving = false,
                            )
                        }
                    }
                }
                stateValue.step == DecideStep.PICK -> {
                    updateState {
                        copy(
                            party = snapshot,
                            isLoading = false,
                            isError = false,
                            isSaving = false,
                        )
                    }
                }
                userInitiated -> {
                    updateState { copy(isLoading = false, isError = false, isSaving = false) }
                }
            }
        }
    }

    private fun startPartyPoll(code: String) {
        partyPollJob?.cancel()
        partyPollJob = viewModelScope.launch {
            while (isActive) {
                delay(POLL_MS)
                refreshParty(userInitiated = false)
            }
        }
    }

    private fun leaveAndClose() {
        partyPollJob?.cancel()
        navigator.back()
    }

    private fun TastePickDto.toRef() = TasteTitleRef(
        videoId = videoId,
        isTvShow = isTvShow,
        title = title,
        imageUrl = imageUrl,
    )

    private fun trackError(reason: String) {
        analytics.track(
            AppEvents.tasteError(
                reason = reason,
                step = stateValue.step.name.lowercase(),
            )
        )
    }

    companion object {
        private const val MIN_SEED_GRID = 12
        private const val MAX_SEED_PAGE = 10
        private const val POLL_MS = 2000L
        private const val MIN_JOIN_CODE = 4
        private const val MAX_JOIN_CODE = 12
        private val JOIN_CODE_CHARS = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789".toSet()

        fun normalizePartyCode(raw: String): String =
            raw.trim().uppercase().filter { it in JOIN_CODE_CHARS }.take(MAX_JOIN_CODE)
    }
}
